/**
 * Loading Spinner Component
 * @author Muhammad Arsan
 * @description A reusable Button component
 */

import React from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { buttonRadiusClassName } from "@config/theme/button";
import type { ButtonProps, ButtonVariant } from "./types/button";

const variantClasses: Record<ButtonVariant, string> = {
	primary:
		"bg-primary text-primary-foreground hover:bg-primary-hover font-semibold border-0",
	secondary:
		"bg-primary-soft text-primary-soft-text hover:bg-primary-soft-hover font-semibold border-0",
	icon: "text-foreground hover:text-primary p-1",
	tertiary:
		"bg-white font-semibold text-primary border border-primary hover:bg-primary-soft",
	danger: "bg-danger text-danger-foreground hover:bg-danger-hover font-semibold border-0",
	dangerSecondary:
		"bg-danger text-danger-foreground hover:bg-danger-hover font-semibold border-0",
	dangerTertiary:
		"bg-white font-semibold text-danger border border-danger hover:bg-danger-soft",
};

const Button: React.FC<ButtonProps> = React.memo(
	({
		text,
		variant = "primary",
		loading = false,
		isDisabled = false,
		className = "",
		disabled,
		children,
		...props
	}) => {
		const isButtonDisabled = loading || isDisabled || disabled;

		const baseClasses =
			variant === "icon"
				? "font-sans tracking-wide transition-colors duration-150 flex items-center justify-center"
				: `${buttonRadiusClassName} font-sans tracking-wide px-6 py-3 text-base transition-all duration-200 flex items-center justify-center gap-2 shadow-[0_5px_10px_rgba(0,0,0,0.05)]`;

		const hoverClasses = variant === "icon" ? "" : "hover:shadow-md";
		return (
			<button
				type="button"
				className={`${baseClasses} ${variantClasses[variant]} ${
					isButtonDisabled ? "opacity-60 cursor-not-allowed" : hoverClasses
				} ${className}`}
				disabled={isButtonDisabled}
				{...props}>
				{loading && <CircularProgress size={18} color="inherit" />}
				{children ? children : text}
			</button>
		);
	},
);

export default Button;
